import React, { useMemo } from 'react';
import { Box, Typography, Stack, Chip, Divider, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  deriveOrderVerificationSteps,
  type OrderVerificationStep,
  type VerifiableOrder,
  type VerificationSignature,
  type VerificationStepKey,
} from '../../utils/orderVerificationSteps';

/**
 * GP-1387: per-step verification audit — "who did what check, and when".
 *
 * Shared by the Clinical Verification workspace's Verification tab
 * (`patient-360/VerificationTab`) and the order detail view
 * (`pages/orders/OrderDetail`) so both read the SAME derivation
 * (`utils/orderVerificationSteps`) and can never disagree about who verified an
 * order. Renders nothing but content — the caller supplies the Card/heading.
 */

const STEP_COLORS: Record<VerificationStepKey, string> = {
  data_verification: '#1976d2',
  clinical_verification: '#198754',
  packaging: '#0288d1',
  product_verification: '#ed6c02',
  dispensing: '#9c27b0',
};

/** Timestamps only — never a bare date, so `date-fns` local formatting is correct here. */
const formatStamp = (iso?: string): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : format(date, 'MMM dd, yyyy h:mm a');
};

const formatSignatureStamp = (signedAt?: string | Date): string | null => {
  if (!signedAt) return null;
  const date = signedAt instanceof Date ? signedAt : new Date(signedAt);
  return Number.isNaN(date.getTime()) ? null : format(date, 'MMM dd, h:mm a');
};

interface SignatureMarkProps {
  signature: VerificationSignature;
  color: string;
}

const SignatureMark: React.FC<SignatureMarkProps> = ({ signature, color }) => {
  const stamp = formatSignatureStamp(signature.signedAt);
  const signer = signature.userName || signature.userEmail;
  return (
    <Tooltip
      arrow
      title={[signer, stamp].filter(Boolean).join(' · ') || 'Signature captured'}
    >
      <Box
        sx={{
          px: 1,
          py: 0.5,
          minWidth: 64,
          textAlign: 'center',
          borderRadius: 1.5,
          border: 1,
          borderColor: color,
          bgcolor: `${color}0A`,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.95rem',
            lineHeight: 1.2,
            color,
            fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
            fontStyle: 'italic',
          }}
        >
          {signature.initials || '—'}
        </Typography>
        {signature.rxNumber != null && String(signature.rxNumber).trim() !== '' && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.6rem', display: 'block' }}
          >
            Rx {signature.rxNumber}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

interface StepRowProps {
  step: OrderVerificationStep;
}

const StepRow: React.FC<StepRowProps> = ({ step }) => {
  const color = STEP_COLORS[step.key];
  const stamp = formatStamp(step.performedAt);

  return (
    <Box data-testid={`verification-step-${step.key}`} sx={{ py: 1.25 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
      >
        <Box sx={{ minWidth: 200, flex: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <CheckCircleIcon sx={{ fontSize: 18, color }} />
            <Typography variant="subtitle2" fontWeight={700}>
              {step.label}
            </Typography>
            {step.detail && (
              <Chip label={step.detail} size="small" variant="outlined" sx={{ height: 20 }} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {/* No silent fallbacks: an unrecorded performer says so rather than
                borrowing a neighbouring step's user. */}
            {step.performedBy ? (
              <>
                By <strong>{step.performedBy}</strong>
              </>
            ) : (
              'Performed by not recorded'
            )}
            {stamp ? ` · ${stamp}` : ' · time not recorded'}
          </Typography>
        </Box>

        {step.signatures.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {step.signatures.map((signature, index) => (
              <SignatureMark
                key={`${signature.rxNumber ?? 'rx'}-${index}`}
                signature={signature}
                color={color}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export interface OrderVerificationSummaryProps {
  /** Order carrying the verification metadata/columns. */
  order: VerifiableOrder | null | undefined;
  /** Signatures from `/api/rx/:pharmacyId/signatures-batch` for this order's Rx numbers. */
  signatures?: VerificationSignature[];
  /** Shown when the order genuinely has zero verification events. */
  emptyMessage?: string;
  /** Suppress the built-in empty state (caller renders its own). */
  hideWhenEmpty?: boolean;
}

const OrderVerificationSummary: React.FC<OrderVerificationSummaryProps> = ({
  order,
  signatures = [],
  emptyMessage = 'No verification steps have been completed for this order yet.',
  hideWhenEmpty = false,
}) => {
  const steps = useMemo(
    () => deriveOrderVerificationSteps(order, signatures),
    [order, signatures],
  );

  if (steps.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <Box
        data-testid="order-verification-empty"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1 }}
      >
        <VerifiedIcon sx={{ fontSize: 40, color: 'grey.300' }} />
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="order-verification-summary">
      {steps.map((step, index) => (
        <React.Fragment key={step.key}>
          {index > 0 && <Divider />}
          <StepRow step={step} />
        </React.Fragment>
      ))}
    </Box>
  );
};

export default OrderVerificationSummary;
